import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CheckCircle2,
  ChevronDown,
  Copy,
  Edit3,
  FileText,
  MinusCircle,
  Plus,
} from 'lucide-react';
import type {
  Form,
  FormConnectionRef,
  FormConnections,
  FormListQuery,
  FormListResponse,
} from '@proptr/shared';
import { EmptyState } from '../components/EmptyState.js';
import {
  HeaderFilter,
  HeaderLabel,
  TableCard,
  TableHeaderRow,
  TableLoading,
  TableMessage,
  TablePagination,
} from '../components/table/index.js';
import { AppShell } from '../components/layout/AppShell.js';
import { StatusBadge as SharedStatusBadge } from '../components/StatusBadge.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.js';
import { Tooltip } from '../components/ui/tooltip.js';
import { isTestMode } from '../lib/testMode.js';
import { cn } from '../lib/utils.js';
import { client } from '../main.js';

type StatusFilter = NonNullable<FormListQuery['status']>;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'all', label: 'All forms' },
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
];

export function FormsPage() {
  const navigate = useNavigate();
  const [status, setStatus] = React.useState<StatusFilter>('all');
  const [pageSize, setPageSize] = React.useState<number>(25);
  const [page, setPage] = React.useState<number>(1);
  const [data, setData] = React.useState<FormListResponse | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  // True while a filter/sort/page change refetches with rows
  // already on screen — drives TableCard's dim + "Updating" pill.
  const [refreshing, setRefreshing] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [createError, setCreateError] = React.useState<string | null>(null);
  // Bumped by row actions (clone/disable/enable) to refetch the list.
  const [refreshTick, setRefreshTick] = React.useState(0);

  // Create a blank form, then jump straight into its editor (same flow
  // as the affiliate "Create" card). The manager renames + builds it
  // there; an unfinished "Untitled form" can be deleted from the list.
  const handleCreate = async () => {
    if (creating) return;
    setCreating(true);
    setCreateError(null);
    try {
      const created = await client.forms.create({ name: 'Untitled form' });
      navigate(`/forms/${created._id}`);
    } catch (err) {
      setCreateError((err as Error).message);
      setCreating(false);
    }
  };

  React.useEffect(() => {
    let alive = true;
    setError(null);
    setRefreshing(true);
    client.forms
      .list({
        status,
        sort: 'createdAt',
        order: 'desc',
        page,
        limit: pageSize,
      })
      .then((res) => {
        if (alive) setData(res);
      })
      .catch((err: Error) => {
        if (alive) setError(err.message);
      })
      .finally(() => {
        if (alive) setRefreshing(false);
      });
    return () => {
      alive = false;
    };
  }, [status, page, pageSize, refreshTick]);

  const meta = data?.meta;
  const total = meta?.total ?? 0;
  const totalPages = meta?.pages ?? 1;
  const items = data?.items ?? null;
  const counts = meta?.counts;

  return (
    <AppShell>
      <div className="flex flex-1 flex-col space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-6">
          <div className="min-w-0">
            <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
              Forms
            </h1>
            <p className="mt-3 font-mono text-[11px] text-muted-foreground">
              {counts ? (
                <>
                  <span className="text-foreground">{counts.all}</span> total
                  <span className="mx-2 text-muted-foreground/60">·</span>
                  <span className="text-foreground">{counts.active}</span> active
                  <span className="mx-2 text-muted-foreground/60">·</span>
                  <span className="text-foreground">{counts.disabled}</span>{' '}
                  disabled
                </>
              ) : (
                'Loading…'
              )}
            </p>
          </div>

          <NewFormCard
            onCreate={handleCreate}
            creating={creating}
            createError={createError}
          />
        </div>

        <TableCard
          minWidth={1000}
          busy={refreshing && items !== null}
          footer={
            <TablePagination
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(1);
              }}
            />
          }
        >
          <TableHeaderRow>
            <HeaderFilter
              className="min-w-[260px] flex-[3_1_260px]"
              value={status}
              options={STATUS_OPTIONS}
              onChange={(v) => {
                setStatus(v);
                setPage(1);
              }}
              menuClassName="min-w-[10rem]"
            />
            <HeaderLabel className="min-w-[120px] flex-[1_1_120px]">
              Questions
            </HeaderLabel>
            <HeaderLabel className="min-w-[90px] flex-[1_1_90px]">
              Opened
            </HeaderLabel>
            <HeaderLabel className="min-w-[90px] flex-[1_1_90px]">
              Started
            </HeaderLabel>
            <HeaderLabel className="min-w-[90px] flex-[1_1_90px]">
              Finished
            </HeaderLabel>
            <HeaderLabel className="min-w-[200px] flex-[2_1_200px]">
              Connected to
            </HeaderLabel>
            <HeaderLabel className="min-w-[120px] flex-[1_1_120px]">
              Status
            </HeaderLabel>
            <HeaderLabel className="min-w-[140px] flex-[1_1_140px]">
              Created
            </HeaderLabel>
          </TableHeaderRow>

          {error ? (
            <TableMessage tone="destructive">{error}</TableMessage>
          ) : items === null ? (
            <TableLoading />
          ) : items.length === 0 ? (
            <EmptyState
              icon={FileText}
              title={status === 'disabled' ? 'No disabled forms' : 'No forms yet'}
              description={
                status === 'disabled'
                  ? 'Forms you disable will land here. Disabled forms stop accepting submissions.'
                  : "This is where you'll create and manage the forms attached to your proptrees — contact requests, viewings, callback bookings."
              }
            />
          ) : (
            items.map((form) => (
              <FormRow
                key={form._id}
                form={form}
                onChanged={() => setRefreshTick((t) => t + 1)}
              />
            ))
          )}
        </TableCard>
      </div>
    </AppShell>
  );
}

function FormRow({ form, onChanged }: { form: Form; onChanged: () => void }) {
  const name = form.name || '—';
  const questionCount = Array.isArray(form.questions)
    ? form.questions.length
    : 0;
  // Funnel counters are optional until the API aggregates them.
  const stat = (n: number | undefined) =>
    n === undefined ? '—' : n.toLocaleString();
  const isDisabled = form.disabled === true;

  return (
    <div className="flex items-center gap-4 border-b border-[hsl(var(--hairline))] px-5 py-4 transition-colors last:border-b-0 hover:bg-muted/60">
      <FormRowMenu form={form} onChanged={onChanged} />
      <Link
        to={`/forms/${form._id}`}
        className="flex min-w-0 flex-1 items-center gap-4"
      >
      <div className="min-w-[212px] flex-[3_1_212px] truncate font-sans text-[14px] font-medium text-foreground">
        {name}
      </div>
      <div className="min-w-[120px] flex-[1_1_120px] truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80">
        {questionCount}
      </div>
      <div className="min-w-[90px] flex-[1_1_90px] truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80">
        {stat(form.stats?.opened)}
      </div>
      <div className="min-w-[90px] flex-[1_1_90px] truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80">
        {stat(form.stats?.started)}
      </div>
      <div className="min-w-[90px] flex-[1_1_90px] truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80">
        {stat(form.stats?.finished)}
      </div>
      <div className="flex min-w-[200px] flex-[2_1_200px] justify-start">
        <ConnectedCell connectedTo={form.connectedTo} />
      </div>
      <div className="min-w-[120px] flex-[1_1_120px]">
        <StatusBadge disabled={isDisabled} />
      </div>
      <div className="min-w-[140px] flex-[1_1_140px] truncate font-mono text-[12px] font-medium tabular-nums text-foreground/80">
        {formatDate(form.createdAt)}
      </div>
      </Link>
    </div>
  );
}

// Chevron actions dropdown at the left of each row — same trigger/menu
// chrome as the affiliate list. Clone and disable/enable are locked in
// test mode (mutations pass through to the real API).
function FormRowMenu({
  form,
  onChanged,
}: {
  form: Form;
  onChanged: () => void;
}) {
  const navigate = useNavigate();
  const testMode = isTestMode();
  const [busy, setBusy] = React.useState(false);
  const isDisabled = form.disabled === true;

  const handleClone = async () => {
    if (busy) return;
    setBusy(true);
    try {
      // Land in the copy's editor so it can be renamed immediately.
      const copy = await client.forms.clone(form._id);
      navigate(`/forms/${copy._id}`);
    } catch (err) {
      window.alert((err as Error).message);
      setBusy(false);
    }
  };

  const handleStatus = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (isDisabled) await client.forms.enable(form._id);
      else await client.forms.disable(form._id);
      onChanged();
    } catch (err) {
      window.alert((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="group flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus:outline-none focus-visible:bg-muted focus-visible:text-foreground data-[state=open]:bg-muted data-[state=open]:text-foreground"
          aria-label="Form actions"
        >
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-data-[state=open]:rotate-180" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-[14rem]">
        <DropdownMenuItem onSelect={() => navigate(`/forms/${form._id}`)}>
          <span className="flex-1">Edit</span>
          <Edit3 />
        </DropdownMenuItem>
        <DropdownMenuItem
          onSelect={() => void handleClone()}
          disabled={busy || testMode}
        >
          <span className="flex-1">Clone</span>
          <Copy />
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {isDisabled ? (
          <DropdownMenuItem
            onSelect={() => void handleStatus()}
            disabled={busy || testMode}
            className="text-emerald-600 focus:text-emerald-700 [&_svg]:text-emerald-600"
          >
            <span className="flex-1">Enable</span>
            <CheckCircle2 />
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem
            onSelect={() => void handleStatus()}
            disabled={busy || testMode}
            className="text-destructive focus:text-destructive [&_svg]:text-destructive"
          >
            <span className="flex-1">Disable</span>
            <MinusCircle />
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Compact "Create new form" card next to the page title. Mirrors the
// affiliate page's NewAffiliateCard. Forms have no quota, so the
// subtitle just explains what forms are for (or surfaces a create error).
function NewFormCard({
  onCreate,
  creating,
  createError,
}: {
  onCreate: () => void;
  creating: boolean;
  createError: string | null;
}) {
  return (
    <Card className="flex w-[380px] items-center gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="font-sans text-[13px] font-semibold leading-tight text-foreground">
          New form
        </div>
        <p
          className={cn(
            'mt-1 font-sans text-[11px] leading-snug',
            createError ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {createError ??
            'Qualify leads with a contact, viewing or callback form.'}
        </p>
      </div>
      {isTestMode() ? (
        <Tooltip
          label="Can't create form in test mode"
          className="cursor-not-allowed"
        >
          <Button size="sm" variant="outline" disabled>
            <Plus />
            Create
          </Button>
        </Tooltip>
      ) : (
        <Button
          size="sm"
          variant="outline"
          onClick={onCreate}
          disabled={creating}
        >
          <Plus />
          {creating ? 'Creating…' : 'Create'}
        </Button>
      )}
    </Card>
  );
}

// Compact "connected to" chips. Agency is a plain label (there's only
// ever one agency proptree); agents/affiliates show a count and reveal
// the proptree names on hover. Empty → a muted dash.
function ConnectedCell({
  connectedTo,
}: {
  connectedTo?: FormConnections;
}) {
  const agency = connectedTo?.agency ?? [];
  const agents = connectedTo?.agents ?? [];
  const affiliates = connectedTo?.affiliates ?? [];
  const none =
    agency.length === 0 && agents.length === 0 && affiliates.length === 0;

  if (none) {
    return (
      <span className="font-mono text-[11px] text-muted-foreground/50">—</span>
    );
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5">
      {agency.length > 0 && <ConnChip label="Agency" refs={agency} />}
      {agents.length > 0 && (
        <ConnChip label="Agents" refs={agents} count />
      )}
      {affiliates.length > 0 && (
        <ConnChip label="Affiliates" refs={affiliates} count />
      )}
    </div>
  );
}

function ConnChip({
  label,
  refs,
  count = false,
}: {
  label: string;
  refs: FormConnectionRef[];
  count?: boolean;
}) {
  // Hover reveals which proptrees — newline-joined so the native
  // tooltip lists them one per line.
  const title = refs.map((r) => r.label).join('\n');
  return (
    <span
      title={title}
      className="inline-flex cursor-default items-center gap-1 rounded-full border border-border bg-muted/40 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.06em] text-muted-foreground"
    >
      {label}
      {count && (
        <span className="text-foreground/70">{refs.length}</span>
      )}
    </span>
  );
}

function StatusBadge({ disabled }: { disabled: boolean }) {
  return (
    <SharedStatusBadge
      label={disabled ? 'Disabled' : 'Active'}
      tone={disabled ? 'muted' : 'success'}
    />
  );
}

function formatDate(d: string | Date | null | undefined): string {
  if (!d) return '—';
  const date = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
